import { Box } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo } from "react";
import { AddNurse } from "@/components/AddNurse/AddNurse";
import { usePickOpenShift } from "@/components/OpenShiftCard/hooks";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { FEATURE_FLAGS } from "@/contexts/FeatureFlagsProvider";
import { useDepartmentNurseTypes } from "@/hooks/useDepartmentNurseTypes";
import { useFeatureFlag } from "@/hooks/useOpenFeatureFlag";
import { useScheduleDayOf } from "@/hooks/useScheduleDayOf";
import { SkeletonWithRandomWidths } from "@/pages/Profile/v2/util/constants";
import { DeferMount } from "@/ui/DeferMount/DeferMount";
import { MultiSizePopup } from "@/ui/Popups/MultiSizePopup/MultiSizePopup";
import { MultiSizePopupState } from "@/ui/Popups/MultiSizePopup/utils";
import { NotificationType, type ReplaceShiftRequest, type ShiftRequest } from "@/utils/models";
import { rolesTakingRequest } from "@/utils/roles";
import { useRequestUtils } from "../Provider/useRequestUtils";
import { useManualAddCompletion } from "./useManualAddCompletion";

type Props = {
	open: boolean;
	onClose: () => void;
	approvedNursesIds: number[];
	request: ShiftRequest;
};

export const MinimizableManualAddStaff: FC<Props> = ({ open, onClose, request, approvedNursesIds }) => {
	const { track } = useAnalytics();
	const { chosenDepartment } = useChosenDepartment();
	const scheduleDayOf = useScheduleDayOf();
	const { completeManualAdd, isCompleting, isCompletionSuccess } = useManualAddCompletion();
	const { mutateAsync: pickOpenShift } = usePickOpenShift();
	const { approveRequest } = useRequestUtils();
	const isReplaceRequest = request.request_type === NotificationType.ReplaceShift;

	const handleClose = useCallback(() => {
		if (isCompleting) return; // Prevent closing while deleting or updating
		onClose();
	}, [onClose, isCompleting]);

	const handleAddCompletedForReplace = useCallback(
		async (nurseId: number) => {
			await pickOpenShift({
				openShiftId: request.id,
				nurseId: nurseId,
				selectedStart: request.message.shift_start,
				selectedEnd: request.message.shift_end,
				departmentId: chosenDepartment.department_id,
			});
			await approveRequest(request, nurseId);
			track("ManualStaffReplace_Completed", {
				source: "MinimizableManualAddStaff",
				requestId: request.id,
			});
		},
		[pickOpenShift, request, approveRequest, track, chosenDepartment],
	);

	const handleAddCompletedForOpenShift = useCallback(async () => {
		await completeManualAdd({
			request,
			approvedNursesCount: approvedNursesIds.length,
			source: "MinimizableManualAddStaff",
		});
	}, [request, approvedNursesIds, completeManualAdd]);

	const handleAddCompleted = useCallback(
		async (didAddStaffOrNurseId: boolean | number) => {
			if (typeof didAddStaffOrNurseId === "number" && isReplaceRequest) {
				await handleAddCompletedForReplace(didAddStaffOrNurseId);
			} else if (didAddStaffOrNurseId) {
				await handleAddCompletedForOpenShift();
			}
		},
		[handleAddCompletedForReplace, isReplaceRequest, handleAddCompletedForOpenShift],
	);

	useEffect(() => {
		if (isCompletionSuccess) {
			handleClose();
		}
	}, [isCompletionSuccess, handleClose]);

	// `message.date` is the shift's start time; an early-morning shift belongs to the previous schedule day. The panel
	// finds the day itself, so a request on any day of the schedule opens.
	const { date: requestDate, shiftType: requestShiftType } = scheduleDayOf({ startTime: request.message.shift_start, shiftType: request.message.shift_type });

	const nurseIdsToIgnore = useMemo(() => (isReplaceRequest ? [(request as ReplaceShiftRequest).replacement_nurse_id] : undefined), [isReplaceRequest, request]);
	// Whoever can claim the request, as pickup decides: in a job department its job holders of every role (the list
	// narrows to `requestJobId`) — not the role group it was posted with, which leaves out e.g. an MT holding the
	// Nurse job; else the roles it names, or every role when it names none.
	const departmentNurseTypes = useDepartmentNurseTypes(chosenDepartment.department_id);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const rolesToDisplay = useMemo(
		() => rolesTakingRequest(request.message.roles, departmentNurseTypes, jobManagementEnabled),
		[request.message.roles, departmentNurseTypes, jobManagementEnabled],
	);

	return (
		<MultiSizePopup
			title={"Manual Staff Addition"}
			open={open}
			onClose={handleClose}
			disableMinimize={true}
			bodyRenderer={(currentSizeState) => {
				switch (currentSizeState) {
					case MultiSizePopupState.MAXIMIZED:
						return (
							<Box
								sx={{
									maxWidth: "1200px",
								}}
							>
								<DeferMount
									fallback={
										<Box
											sx={{
												minWidth: "800px",
											}}
										>
											<SkeletonWithRandomWidths randomLines={true} lines={10} header={true} />
										</Box>
									}
								>
									<AddNurse
										setOpen={(_) => {}}
										setIsAdd={handleAddCompleted}
										onNurseAddedAvoidActualAdding={isReplaceRequest ? handleAddCompleted : undefined}
										date={requestDate}
										shiftType={requestShiftType}
										onClose={handleClose}
										rolesToDisplay={rolesToDisplay}
										lockedTimes={{
											startTime: request.message.shift_start,
											endTime: request.message.shift_end,
										}}
										nurseIdsToIgnore={nurseIdsToIgnore}
										requestJobId={request.job_id}
										skipRedundantOpenShiftSuggestion={true}
										// A request is filled by exactly one nurse.
										selectionMode="single"
									/>
								</DeferMount>
							</Box>
						);
				}
				return null;
			}}
		/>
	);
};
